/**
 * JavaScript side adapter between TypeScript audio player controls (AudioDoc,
 * PlaybackControl, chapter navigation, ...) and our Swift plugin that
 * implements native audio playback on iOS
 * (mobile/ios/App/App/NativeAudioPlugin.swift)
 *
 * This module wraps the Swift plugin to have a similar API to an
 * HTMLAudioElement, which lets the player controls work the same whether using
 * native mobile audio or web audio.
 */

import type { NativePlaybackProgress } from './native-audio.types'
import type { AudioEngine } from './audio-engine'
import { Capacitor, registerPlugin } from '@capacitor/core'
import type { PluginListenerHandle } from '@capacitor/core'
import type { DocWithContent } from '@/models/interfaces'
import { offline } from '@/services/offline'
import { chapterTimings } from './chapter-timing'
import { coverArtworkUrl } from './artwork'

// Most successful native commands return playback state from Swift in this
// shape, and native `state` events are emitted with the same data.
// It represents complete information about the current playback state.
export interface NativeAudioSnapshot {
  sessionId: string
  revision: number
  intentRevision: number
  seekRevision: number
  time: number
  duration: number
  paused: boolean
  rate: number
  volume: number
  ready: boolean
  playing: boolean
  ended: boolean
  error: string
}

// The data sent to Swift when loading a recording before playback
export interface NativeAudioPrepareOptions {
  sessionId: string
  url: string
  document: NativePlaybackProgress['document']
  author: string
  artwork: string
  time: number
  rate: number
  volume: number
  offline: boolean
}

// The interface implemented by our Swift plugin, i.e., functions that we can
// call via the Capacitor bridge.
interface NativeAudioPlugin {
  pendingProgress(options: {
    userId: string
  }): Promise<{ progress: NativePlaybackProgress[] }>
  clearProgress(options: {
    userId: string
    id: string
    chapterIds?: string[]
  }): Promise<void>
  resumePosition(options: {
    userId: string
    docId: string
    allowAcknowledged: boolean
  }): Promise<{ position: number | null }>

  prepare(options: NativeAudioPrepareOptions): Promise<NativeAudioSnapshot>
  play(options: { sessionId: string }): Promise<NativeAudioSnapshot>
  pause(options: { sessionId: string }): Promise<NativeAudioSnapshot>
  seek(options: {
    sessionId: string
    time: number
  }): Promise<NativeAudioSnapshot>
  setRate(options: {
    sessionId: string
    rate: number
  }): Promise<NativeAudioSnapshot>
  setVolume(options: {
    sessionId: string
    volume: number
  }): Promise<NativeAudioSnapshot>
  snapshot(options: { sessionId: string }): Promise<NativeAudioSnapshot>
  stop(options: { sessionId: string }): Promise<void>
  addListener(
    event: 'state',
    listener: (state: NativeAudioSnapshot) => void,
  ): Promise<PluginListenerHandle>
}

// Create the JavaScript proxy used to call the native plugin.
export const nativeAudioBridge =
  registerPlugin<NativeAudioPlugin>('NativeAudio')

const native = nativeAudioBridge

export const isNativeAudioAvailable = (): boolean =>
  Capacitor.getPlatform() === 'ios' &&
  Capacitor.isPluginAvailable('NativeAudio')

// The main adapter that presents an HTMLAudioElement-like API.
export class NativeAudio extends EventTarget implements AudioEngine {
  readonly cacheSourceOnPlay = false
  readonly playBeforeCanPlay = true

  // Session IDs ensure that Swift and TypeScript are talking
  // to the version that they expect to be talking to and there is no confusion
  // with multiple concurrent players (e.g. when the user navigates between
  // guides, or some async command finishes out of order)
  readonly sessionId = `${Date.now()}-${Math.random().toString(36).slice(2)}`
  duration = 0
  paused = true

  // Latest playback position received from native code or requested locally.
  private _currentTime = 0

  // Playback rate requested by the UI.
  // The initial value of 1.0 here is a neutral default that matches browser
  // beahviour, but in practice control.rate.ts replaces it with DEFAULT_RATE
  // 1.1 before the audio actually loads
  private _playbackRate = 1

  // Volume requested by the UI
  private _volume = 1

  // Latest native state information accepted; older snapshots are ignored,
  // to prevent becoming confused by out of order data
  private revision = -1

  // Latest native play/pause intent revision. Intent here means that the user
  // made some external command from the lock screen, CarPlay, etc. We give
  // these commands sequence numbers to avoid confusion from out-of-order
  // commands.
  private intentRevision?: number

  // Sequence number for native code seeks, allowing remote seeks to produce the
  // same events as seeks initiated in JavaScript.
  private seekRevision?: number

  // Changes whenever a lock-screen or other native command supersedes queued
  // play/pause work from the UI. It's a sequence counter, used to invalidate
  // stale commands
  private nativeIntentGeneration = 0

  // Whether the plugin has loaded the audio. We use this to emit `canplay`.
  private ready = false

  // Whether the plugin is playing, according to the most recent state update
  private playing = false

  // Whether the audio has completed playing, according to the most recent
  // state update
  ended = false

  // Player UI calls `remove()` when changing guides or closing the player.
  // Track disposal so late asynchronous work cannot issue more commands.
  private disposed = false

  // Latest native preparation promise; commands await it before using the player.
  private loading?: Promise<void>

  // Whether the latest preparation failed, allowing `load()` to retry it.
  private loadFailed = false

  // Promise chain that sends native commands in the order they were requested.
  private queue = Promise.resolve()

  // Subscription used to receive native state changes
  private listener?: PluginListenerHandle

  // ID of the latest queued seek, or zero when no seek is pending.
  private pendingSeek = 0

  // Counts of queued native commands whose snapshots may contain stale settings
  private pendingRateCommands = 0
  private pendingVolumeCommands = 0

  /**
   * @param src Remote URL or downloaded file URI to play.
   * @param doc Document metadata used for chapters and native progress.
   * @param start Initial playback position in seconds.
   * @param isPlaybackFromDownload Whether the source is a downloaded file.
   * @param userId User to whom native playback progress must be saved.
   */
  constructor(
    readonly src: string,
    private doc: DocWithContent,
    start: number,
    private isPlaybackFromDownload: boolean,
    private userId: string,
  ) {
    super()
    if (!userId) {
      throw new Error('Native audio requires a user ID')
    }
    this._currentTime = start
    document.addEventListener('visibilitychange', this.refresh)
  }

  get currentTime(): number {
    return this._currentTime
  }

  set currentTime(value: number) {
    const time = Math.max(0, value)
    if (time === this._currentTime) {
      return
    }
    this._currentTime = time
    const target = this._currentTime
    const seekId = ++this.pendingSeek
    this.emit('seeking')
    this.ignoreReportedFailure(this.queueSeek(target, seekId))
  }

  private async queueSeek(target: number, seekId: number): Promise<void> {
    try {
      await this.enqueue(async () => {
        if (seekId !== this.pendingSeek) {
          return
        }
        const state = await native.seek({
          sessionId: this.sessionId,
          time: target,
        })
        if (seekId === this.pendingSeek) {
          this.pendingSeek = 0
          this.accept(state, true)
          this.emit('seeked')
        }
      })
    } finally {
      if (seekId === this.pendingSeek) {
        this.pendingSeek = 0
        this.emit('seeked')
      }
    }
  }

  get playbackRate(): number {
    return this._playbackRate
  }

  set playbackRate(value: number) {
    this._playbackRate = value
    this.emit('ratechange')
    if (this.loading) {
      this.ignoreReportedFailure(this.setNativeRate(value))
    }
  }

  get volume(): number {
    return this._volume
  }

  set volume(value: number) {
    this._volume = Math.min(1, Math.max(0, value))
    this.emit('volumechange')
    if (this.loading) {
      this.ignoreReportedFailure(this.setNativeVolume(this._volume))
    }
  }

  private async setNativeRate(rate: number): Promise<void> {
    this.pendingRateCommands++
    try {
      await this.enqueue(() =>
        native.setRate({ sessionId: this.sessionId, rate }),
      )
    } finally {
      this.pendingRateCommands--
    }
  }

  private async setNativeVolume(volume: number): Promise<void> {
    this.pendingVolumeCommands++
    try {
      await this.enqueue(() =>
        native.setVolume({ sessionId: this.sessionId, volume }),
      )
    } finally {
      this.pendingVolumeCommands--
    }
  }

  private emit(name: string): void {
    this.dispatchEvent(new Event(name))
  }

  private fail(error: unknown): void {
    console.error('Native audio command failed', error)
    this.emit('error')
  }

  private enqueue(
    action: () => Promise<NativeAudioSnapshot | void>,
  ): Promise<void> {
    const command = this.runQueued(this.queue, action)
    this.queue = command.catch(() => undefined)
    return command
  }

  private ignoreReportedFailure(command: Promise<void>): void {
    void command.catch(() => undefined)
  }

  /* Execute exactly one queued command from the typescript player control UI
   *
   * `runQueued` is responsible for serialisation: waiting for previous commands
   * to finish, waiting for the native plugin to be ready, and ensuring that
   * that the wrapper itself is loaded.
   */
  private async runQueued(
    previous: Promise<void>,
    action: () => Promise<NativeAudioSnapshot | void>,
  ): Promise<void> {
    try {
      await previous
      await this.loading
      if (!this.disposed && !this.loadFailed) {
        const state = await action()
        if (state) {
          this.accept(state)
        }
      }
    } catch (error) {
      this.fail(error)
      throw error
    }
  }

  load(): void {
    if (this.disposed || (this.loading && !this.loadFailed)) {
      return
    }
    const previousLoad = this.loading
    this.loadFailed = false
    this.emit('loadstart')
    this.loading = this.prepare(previousLoad)
  }

  private async prepare(previousLoad?: Promise<void>): Promise<void> {
    try {
      await previousLoad
      if (!this.listener) {
        this.listener = await native.addListener('state', (state) => {
          if (!document.hidden) {
            this.accept(state)
          }
        })
      }
      if (this.disposed) {
        return
      }
      const state = await native.prepare({
        sessionId: this.sessionId,
        url: this.src,
        document: {
          userId: this.userId,
          docId: this.doc.id,
          title: this.doc.title,
          isFree: this.doc.is_free,
          isDownload: this.isPlaybackFromDownload,
          chapters: chapterTimings(this.doc).map(({ idx, start, end }) => {
            const page = this.doc.content[idx]
            return {
              id: page.id,
              title: page.title,
              start,
              end,
              isChapter: page.content_type === 'chapter',
            }
          }),
        },
        author: this.doc.author,
        artwork: coverArtworkUrl(this.doc),
        time: this._currentTime,
        rate: this._playbackRate,
        volume: this._volume,
        offline: offline.isOffline,
      })
      this.accept(state)
    } catch (error) {
      this.loadFailed = true
      this.emit('intentchange')
      this.fail(error)
    }
  }

  async play(): Promise<void> {
    this.load()
    const nativeIntentGeneration = this.nativeIntentGeneration
    await this.enqueue(() =>
      nativeIntentGeneration === this.nativeIntentGeneration
        ? native.play({ sessionId: this.sessionId })
        : Promise.resolve(),
    )
  }

  async pause(): Promise<void> {
    if (this.loading) {
      const nativeIntentGeneration = this.nativeIntentGeneration
      await this.enqueue(() =>
        nativeIntentGeneration === this.nativeIntentGeneration
          ? native.pause({ sessionId: this.sessionId })
          : Promise.resolve(),
      )
    }
  }

  private refresh = (): void => {
    if (!document.hidden && this.loading) {
      this.ignoreReportedFailure(
        this.enqueue(() => native.snapshot({ sessionId: this.sessionId })),
      )
    }
  }

  /**
   *  Receive a state update snapshot from the swift plugin and update
   *  our local state to match it, rejecting invalid updates from another
   *  session or an older revision than we already have.
   */
  private accept(state: NativeAudioSnapshot, localSeek = false): void {
    if (
      this.disposed ||
      state.sessionId !== this.sessionId ||
      state.revision <= this.revision
    ) {
      return
    }
    this.revision = state.revision
    const paused = this.paused,
      ended = this.ended,
      rate = this._playbackRate,
      volume = this._volume
    const seekChanged =
      this.seekRevision !== undefined &&
      state.seekRevision !== this.seekRevision
    this.seekRevision = state.seekRevision
    const remoteSeek = seekChanged && !localSeek && !this.pendingSeek
    if (remoteSeek) {
      this.emit('seeking')
    }
    this.paused = state.paused
    this.ended = state.ended
    this.duration = state.duration
    if (!this.pendingSeek && (state.ready || this.ready)) {
      this._currentTime = state.time
    }
    if (!this.pendingRateCommands) {
      this._playbackRate = state.rate
    }
    if (!this.pendingVolumeCommands) {
      this._volume = state.volume
    }
    const intentChanged =
      this.intentRevision !== undefined &&
      state.intentRevision !== this.intentRevision
    this.intentRevision = state.intentRevision
    if (intentChanged) {
      this.nativeIntentGeneration++
    }
    if (intentChanged || state.error) {
      this.emit('intentchange')
    }
    if (!state.error && state.ready) {
      this.loadFailed = false
      if (!this.ready) {
        this.ready = true
        this.emit('loadedmetadata')
        this.emit('loadeddata')
        this.emit('canplay')
      }
    }
    if (paused !== this.paused) {
      this.emit(this.paused ? 'pause' : 'play')
    }
    if (state.playing && !this.playing) {
      this.emit('playing')
    }
    this.playing = state.playing
    if (rate !== this._playbackRate) {
      this.emit('ratechange')
    }
    if (volume !== this._volume) {
      this.emit('volumechange')
    }
    if (state.ended && !ended) {
      this.emit('ended')
    }
    if (remoteSeek) {
      this.emit('seeked')
    }
    if (state.error) {
      if (this.ready && !this.pendingSeek) {
        this.emit('timeupdate')
      }
      if (!state.ready) {
        this.ready = false
        this.loadFailed = true
      }
      this.emit('error')
      return
    }
    if (this.ready && !this.pendingSeek) {
      this.emit('timeupdate')
      if (!state.paused && !state.playing && !state.ended) {
        this.emit('waiting')
      }
    }
  }

  async remove(): Promise<void> {
    this.disposed = true
    document.removeEventListener('visibilitychange', this.refresh)
    await this.loading
    if (this.loading) {
      await native.stop({ sessionId: this.sessionId })
    }
    await this.queue
    await this.listener?.remove()
  }
}
